// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useRouter } from 'next/router';
import styled from '@emotion/styled';

import Headline from 'presentations/views/components/atoms/Headline';
import { mediaMax, spacing } from 'presentations/views/styles';
import TextField from 'presentations/views/components/atoms/TextField';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { ErrorList } from 'domains/models';
import { homePath, signUpPath } from 'presentations/routes/path';
import {
  primary,
  gray04,
  gray01,
  gray02,
} from 'presentations/views/styles/color';
import SocialLoginButtonList from 'presentations/views/components/organisms/SocialLoginButtonList';

interface Props {
  isLoading: boolean;
  onSubmit?: any;
  onChange?: any;
  onClickService?: any;
  email?: string | null;
  errors: ErrorList;
  to: string;
  isMobileAppUser?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  return (
    <form onSubmit={props.onSubmit}>
      <Container>
        <HeadlineContainer isMobileAppUser={props.isMobileAppUser}>
          <Headline variant="h2">{t('title')}</Headline>
        </HeadlineContainer>
        {!props.isMobileAppUser && (
          <>
            <SocialButtonContainer>
              <SocialLoginButtonList
                isLoading={props.isLoading}
                loadingProvider={null}
                isLogin={true}
                providers={null}
                onClickService={props.onClickService}
              />
            </SocialButtonContainer>
            <TextOther>OR</TextOther>
          </>
        )}
        <FormField>
          <TextField
            type="email"
            width={300}
            name="email"
            placeholder={t('description')}
            value={props.email || ''}
            onChange={props.onChange}
            errors={props.errors['email'] || []}
          />
        </FormField>
        <SubmitContainer>
          <SubmitButton
            size="large"
            color="primary"
            type="submit"
            isLoading={props.isLoading}
          >
            {t('next')}
          </SubmitButton>
        </SubmitContainer>
        {!props.isMobileAppUser && (
          <>
            <AuthDescription>
              <AuthDescriptionIcon name="chevron-right" />
              <AuthDescriptionText
                href={signUpPath({
                  to: props.to,
                  locale: router.locale,
                })}
                withoutNextLink
              >
                {t("don't have an account")}
              </AuthDescriptionText>
            </AuthDescription>
            <ToTopDescription>
              <ToTopDescriptionIcon name="chevron-left" />
              <ToTopDescriptionText href={homePath()}>
                {t('return to the top')}
              </ToTopDescriptionText>
            </ToTopDescription>
          </>
        )}
      </Container>
    </form>
  );
}

const AuthDescriptionIcon = styled(Icon)`
  color: ${primary};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Text = styled.span`
  margin-bottom: ${spacing(24)};
`;
const HeadlineContainer = styled.div<{ isMobileAppUser?: boolean }>`
  margin-top: ${({ isMobileAppUser }) =>
    isMobileAppUser ? spacing(32) : '90px'};
  margin-bottom: ${spacing(32)};
`;
const FormField = styled.div`
  margin-bottom: ${spacing(24)};
`;
const SubmitContainer = styled.div`
  margin-bottom: ${spacing(32)};
`;
const AuthDescriptionText = styled(Link)`
  cursor: pointer;
  color: ${gray01};
`;
const AuthDescription = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0;
`;
const ToTopDescription = styled.div`
  margin-top: ${spacing(32)};
  margin-bottom: ${spacing(72)};
  padding-top: ${spacing(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${gray04};
  width: 300px;
  ${mediaMax.smartphone} {
    width: 240px;
    margin-bottom: ${spacing(32)};
  }
`;
const ToTopDescriptionIcon = styled(Icon)`
  color: ${primary};
`;
const ToTopDescriptionText = styled(Link)`
  color: ${gray01};
`;
const SubmitButton = styled(Button)`
  width: 300px;
`;

const SocialButtonContainer = styled.div`
  margin-bottom: ${spacing(24)};
`;

const TextOther = styled.p`
  width: 100%;
  color: ${gray02};
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(32)};

  &:before,
  &:after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background: ${gray04};
    display: block;
  }
  &:before {
    margin-right: 1.4em;
  }
  &:after {
    margin-left: 1.4em;
  }
`;
